<template>
  <div
    id="profile-toolbar"
    class="kt-offcanvas-panel d-flex flex-column"
    :class="{'kt-offcanvas-panel--on': profileToolbarVisible}"
  >
    <div class="kt-offcanvas-panel__head">
      <h3 class="kt-offcanvas-panel__title">
        Profiel
      </h3>
      <a
        id="profile-toolbar_close"
        class="kt-offcanvas-panel__close"
        aria-label="Profiel sluiten"
        @click="mainStore.toggleProfileToolbar"
      >
        <i class="far fa-times" />
      </a>
    </div>

    <div class="kt-offcanvas-panel__body">
      <div class="kt-user-card-v3">
        <div class="kt-user-card-v3__avatar">
          <img
            v-if="user.profile_picture_big"
            :src="user.profile_picture_big"
            loading="lazy"
            decoding="async"
            alt="Profiel foto"
          >

          <div
            v-else
            class="kt-media kt-media--xl"
            :class="`user-color-${user.color}`"
          >
            <span>{{ user.initials }}</span>
          </div>
        </div>

        <div class="kt-user-card-v3__details">
          <a class="kt-user-card-v3__name">
            {{ user.name }}
          </a>

          <div class="kt-user-card-v3__desc p-0">
            {{ user.role?.name }}
          </div>

          <div
            class="kt-user-card-v3__desc p-0"
            style="font-size: 0.9rem; opacity: 0.7"
          >
            {{ companyName }}
          </div>
        </div>
      </div>

      <div class="kt-separator kt-separator--space-md kt-separator--border-dashed" />

      <div>
        <a
          href="/profile"
          class="d-flex item item--profile"
        >
          <div class="symbol mr-3 d-flex justify-content-center align-items-center">
            <span>
              <i class="far fa-address-card" />
            </span>
          </div>

          <div class="flex-grow-1 item-text">
            <div class="font-weight-bold">Mijn account</div>
            <div class="text-muted">Profiel bekijken</div>
          </div>
        </a>

        <a
          class="d-flex item item--release-notes"
          @click="mainStore.navigateToReleaseNotes"
        >
          <div class="symbol mr-3 d-flex justify-content-center align-items-center">
            <span>
              <fluent-megaphone-loud28-filled
                width="1.7em"
                height="1.7em"
              />
            </span>
          </div>

          <div class="flex-grow-1 item-text">
            <div class="font-weight-bold">
              Release notes
            </div>
            <div class="text-muted">
              Bekijk de laatste updates
            </div>
          </div>
        </a>

        <a
          v-if="showWorkflowClearBtn"
          class="d-flex item item-workflow-clear"
          @click="clearCurrentWorkflow"
        >
          <div class="symbol mr-3 d-flex justify-content-center align-items-center">
            <span>
              <i class="far fa-warning" />
            </span>
          </div>

          <div class="flex-grow-1 item-text">
            <div class="font-weight-bold">
              Administratie legen
            </div>
          </div>
        </a>
      </div>

      <div class="signout--container">
        <a
          class="btn btn--tertiary btn-font-sm btn-upper btn-bold my-4 w-100"
          @click="mainStore.doLogout"
        >
          <span class="d-flex align-items-center justify-content-center">
            <i class="fas fa-right-from-bracket" />
            <span>Uitloggen</span>
          </span>
        </a>
      </div>
    </div>

    <confirmation ref="confirm" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useMainStore } from '@/stores/main-store'
import { storeToRefs } from 'pinia'
import FluentMegaphoneLoud28Filled from '@/components/icons/FluentMegaphoneLoud28Filled.vue'
import Confirmation from '@/components/Confirmation.vue'
import type { ConfirmationType } from '@/misc/custom-types'
import { HttpPost } from '@/services/Service'
import type CustomWindow from '@/misc/custom-window'
import { showToast } from '@/general'

declare const window: CustomWindow

const mainStore = useMainStore()

const {
  user,
  profileToolbarVisible,
  companyName,
} = storeToRefs(mainStore)

// Only show the clear workflow button for super admins
// in develop and local environments
const showWorkflowClearBtn = computed(() => {
  return user.value.is_super_admin && ['develop', 'local'].includes(window.APP_ENV)
})

const confirm = ref<ConfirmationType>()

const clearCurrentWorkflow = async () => {
  const result = await confirm.value?.open({
    showSwalType: false,
    message: 'Weet je zeker dat je de administratie wilt legen?',
    submitText: 'Legen',
    cancelText: 'Annuleren',
  })

  if (!result?.value) return

  await HttpPost('/api/v1/workflow/clear')
    .then(() => location.reload())
}
</script>

<style scoped lang="scss">
@import "resources/sass/variables";

.kt-offcanvas-panel__body {
  height: 100%;
}

.signout--container {
  position: fixed;
  bottom: 0;
  width: calc(100% - 3rem);
}

.item {
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 4px;

  > .item-text {
    color: #3F4254;
  }

  &:hover {
    background: rgba(243, 246, 249, 0.65);

    > .item-text {
      color: $base-color !important;
    }
  }
}

.item--profile:hover, .item--release-notes:hover {
  text-decoration: none !important;
}

.item--profile .symbol span i {
  color: #F08825 !important;
}

.item-workflow-clear .symbol span i {
  color: red !important;
}

.item--release-notes .symbol span svg {
  color: $base-color !important;
}

.symbol {
  height: 40px;
  width: 40px;
  color: #3F4254;
  background-color: #F3F6F9;
  border-radius: 0.42rem;

  > span > i {
    font-size: 1.4rem;
  }
}

.kt-offcanvas-panel .kt-offcanvas-panel__close:hover {
  text-decoration: none !important;
  background-color: $base-color;
  cursor: pointer;
}

.kt-offcanvas-panel {
  min-width: 320px;
  max-width: 320px !important;
}

.kt-separator.kt-separator--space-md {
  margin-bottom: 1rem;
}

.kt-user-card-v3 {
  display: flex;
  align-items: center;
}

.kt-user-card-v3 .kt-user-card-v3__avatar > img {
  max-height: 100px;
  max-width: 100px;
  border-radius: 7px;
}

.kt-user-card-v3 .kt-user-card-v3__details {
  padding: 1rem;
  height: 100px;
  display: flex;
  flex-direction: column;
}

.kt-user-card-v3 .kt-user-card-v3__details a.kt-user-card-v3__name {
  font-size: 1.2rem;
  font-weight: 500;
  color: #33323f;
  text-decoration: none;
  padding-bottom: 0.8rem;
  transition: all 0.3s;
}

.kt-user-card-v3 .kt-user-card-v3__details a.kt-user-card-v3__name:hover {
  transition: all 0.3s;
  color: $base-color;
}

.kt-user-card-v3 .kt-user-card-v3__details .kt-user-card-v3__desc {
  font-size: 1rem;
  font-weight: 400;
  color: #b2afc6;
  padding: 0.8rem 0;
}

.kt-user-card-v3__name {
  padding-bottom: 0 !important;
}
</style>
